import { ElementType, Intent } from "@mobility-item-page/utilities/client";
import {
  createContext,
  type Dispatch,
  type MouseEventHandler,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAdViewAd } from "src/context/AdViewProviderContext.tsx";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import usePulse from "src/utils/pulse-tracking/usePulse.ts";
import { Fullscreen } from "./Fullscreen.tsx";
import { hasAdSlide, scrollAdIntoView, scrollIntoView } from "./util.ts";

type EventHandler = (event?: Parameters<MouseEventHandler>[0]) => void;

type GalleryState = {
  fullScreen: boolean;
  setFullScreen: Dispatch<SetStateAction<boolean>>;
  currentImageIndex: number;
  setCurrentImageIndex: (value: number) => void;
  showNextImage: EventHandler;
  showPreviousImage: EventHandler;
  overview: boolean;
  toggleOverview: EventHandler;
  galleryRef: RefObject<HTMLDivElement | null>;
};

const initialState: GalleryState = {
  fullScreen: false,
  setFullScreen: () => {},
  currentImageIndex: 0,
  setCurrentImageIndex: () => {},
  showNextImage: () => {},
  showPreviousImage: () => {},
  overview: false,
  toggleOverview: () => {},
  galleryRef: { current: null },
};

const GalleryContext = createContext<GalleryState>(initialState);

export const useGallery = () => {
  return useContext(GalleryContext);
};

export const GalleryProvider = ({ children }: { children: ReactNode }) => {
  const navigationClicked = useRef({ prev: false, next: false });
  const { initialImage } = useAppConfig();
  const [currentImageIndex, setImageIndex] = useState(initialImage ? initialImage - 1 : 0);
  const [fullScreen, setFullScreen] = useState(false);
  const [overview, setOverview] = useState(false);
  const { images } = useAdViewAd();
  const { trackClick } = usePulse();
  const galleryRef = useRef<HTMLDivElement>(null);

  const isGalleryInView = useCallback(() => {
    if (!galleryRef.current) return false;
    const rect = galleryRef.current.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
  }, []);

  const trackViewImage = (direction: "next" | "prev") => {
    // Tracking event: CE_ev06
    const alreadyTracked = navigationClicked.current[direction];
    if (!alreadyTracked) {
      trackClick({
        name: "View carousel image",
        objectId: "CE_ev06",
        elementType: ElementType.Image,
        intent: Intent.View,
      });
      navigationClicked.current[direction] = true;
    }
  };

  const setCurrentImageIndex = (value: number | ((prevValue: number) => number)) => {
    setImageIndex((prev) => {
      const newValue = typeof value === "function" ? value(prev) : value;
      const tempUrl = new URL(window.location.href);
      tempUrl.searchParams.set("ci", (newValue + 1).toString());
      window.history.replaceState(null, "", tempUrl);
      return newValue;
    });
  };

  const toggleOverview: EventHandler = (event) => {
    event?.stopPropagation();
    setOverview((prev) => !prev);
  };

  const showNextImage: EventHandler = (event) => {
    event?.stopPropagation();
    setCurrentImageIndex((currentIndex) => {
      const atLastImage = images.length > 0 && currentIndex === images.length - 1;

      if (fullScreen && hasAdSlide() && atLastImage) {
        scrollAdIntoView();
        return currentIndex;
      }

      trackViewImage("next");
      const nextIndex = currentIndex + 1 < images.length ? currentIndex + 1 : 0;
      scrollIntoView(nextIndex);
      return nextIndex;
    });
  };

  const showPreviousImage: EventHandler = (event) => {
    event?.stopPropagation();
    setCurrentImageIndex((currentIndex) => {
      const atFirstImage = currentIndex === 0;

      if (fullScreen && hasAdSlide() && atFirstImage) {
        scrollAdIntoView();
        return currentIndex;
      }

      trackViewImage("prev");
      const prevIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : images.length - 1;
      scrollIntoView(prevIndex);
      return prevIndex;
    });
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      setFullScreen(false);
    }
    if (fullScreen) {
      return;
    }

    if (!isGalleryInView()) {
      return;
    }

    if (event.key === "ArrowRight") {
      showNextImage();
    }
    if (event.key === "ArrowLeft") {
      showPreviousImage();
    }
  };

  const keydownHandlerRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keydownHandlerRef.current = onKeyDown;

  useEffect(() => {
    const listener = (e: KeyboardEvent) => keydownHandlerRef.current(e);
    window.addEventListener("keydown", listener);
    return () => {
      window.removeEventListener("keydown", listener);
    };
  }, []);

  return (
    <GalleryContext
      value={{
        fullScreen,
        setFullScreen,
        currentImageIndex,
        setCurrentImageIndex,
        showNextImage,
        showPreviousImage,
        overview,
        toggleOverview,
        galleryRef,
      }}
    >
      <div ref={galleryRef}>{fullScreen ? <Fullscreen>{children}</Fullscreen> : children}</div>
    </GalleryContext>
  );
};
